import useSWR from "swr";

import { IS_ADMIN, KEYSTONE, POLICY_ID, USER } from "./ss-data-store";
import {
  BadRequestError,
  type SSError,
  UnauthorizedRequestError,
  isError,
} from "./ss-error";

/**
 * Access Keystone API Client.
 *
 * TODO: deprecate in favor of ss-data-hooks.
 * keystone client will be used there, just not directly in component code.
 */
export const useKeystone = () => {
  return KEYSTONE;
};

/**
 * Read the user, set server-side.
 */
export function useUser() {
  return USER;
}

/**
 * Read the selected policy GUID, always set server-side.
 */
export function usePolicyId() {
  return POLICY_ID;
}

/**
 * Return platform id for a policy. Pulls from currently selected policy by default.
 */
export function usePolicyPlatformId(policyId = POLICY_ID) {
  const { data } = usePoliciesList();
  return data?.find((policy) => policy.keystonePolicyId === policyId)
    ?.platformPolicyId;
}

/**
 * Get current UI events.
 */
export function useUiEvents() {
  const user = useUser();
  const fetcher = async () => {
    if (!user) {
      throw new UnauthorizedRequestError("no authenticated user");
    }
    const res = await KEYSTONE.getUiEvents(user.userId);
    if (isError(res)) {
      throw res;
    }
    return res;
  };
  return useSWR("uievents", fetcher);
}

/**
 * Latest list of policies.
 */
export function usePoliciesList() {
  const user = useUser();
  const fetcher = async () => {
    if (!user) {
      throw new UnauthorizedRequestError("no authenticated user");
    }
    const res = await KEYSTONE.getUser(user.email);
    if (isError(res)) {
      throw res;
    }
    // ADMIN HACK: if user is admin and policy is in server data append missing policy
    if (
      IS_ADMIN &&
      !res.policyList.find((p) => p.keystonePolicyId === POLICY_ID)
    ) {
      const adminPolicy = window.SS_STATE.policies?.find(
        (p) => p.keystonePolicyId === POLICY_ID
      );
      if (adminPolicy) {
        return [...res.policyList, adminPolicy];
      }
    }
    return res.policyList;
  };
  return useSWR("policies", fetcher);
}

/**
 * Get the full policy from keystone.
 */
export function usePolicyDetail(policyId = POLICY_ID) {
  const fetcher = () => {
    if (!policyId) {
      throw new BadRequestError("no policy ID selected");
    }
    return throwSSError(KEYSTONE.getPolicy(policyId));
  };
  return useSWR(["policies", policyId], fetcher);
}

/**
 * Get payment plans and easypay fee for current policy
 */
export function usePaymentPlans() {
  const fetcher = () => {
    if (!POLICY_ID) {
      throw new BadRequestError("no policy ID selected");
    }
    return throwSSError(KEYSTONE.getPaymentPlans(POLICY_ID));
  };
  return useSWR(["policies", POLICY_ID, "payment-plans"], fetcher);
}

/**
 * Constants for insured phone number and other info
 */
export function useClaimsConstants(policyId = POLICY_ID) {
  const fetcher = () => {
    if (!policyId) {
      throw new BadRequestError("no policy ID selected");
    }
    return throwSSError(KEYSTONE.getClaimConstants(policyId));
  };
  return useSWR(["policies", policyId, "claims-constants"], fetcher);
}

/**
 * Get a users latest claims data.
 */
export function useClaimsList() {
  const fetcher = () => {
    if (!POLICY_ID) {
      throw new BadRequestError("no policy ID selected");
    }
    return throwSSError(KEYSTONE.getClaimsList(POLICY_ID));
  };
  return useSWR("claims", fetcher, {
    refreshInterval(list) {
      if (
        list?.ClaimsTrackerInfo?.length &&
        list?.ClaimsTrackerInfo?.some(
          (claim) => claim.CarrierClaim?.Number === "PENDING"
        )
      ) {
        // Check if claims are still pending every 2 seconds
        return 2000;
      }
      return 0;
    },
  });
}

/**
 * Get a policy's latest inspections data.
 */
export function useInspectionsList() {
  const fetcher = () => {
    if (!POLICY_ID) {
      throw new BadRequestError("no policy ID selected");
    }
    return throwSSError(KEYSTONE.getPolicyInspections(POLICY_ID));
  };
  return useSWR("inspections", fetcher);
}

/**
 * Decode data using keystone api. Fetch is skipped if input is falsy.
 */
export function useDecodeData(input: string) {
  const fetcher = () => {
    return throwSSError(KEYSTONE.decodeData(input));
  };
  return useSWR(input ? ["decoded-data", input] : null, fetcher);
}

async function throwSSError<T>(apiCall: Promise<T | SSError>) {
  const res = await apiCall;
  if (isError(res)) {
    throw res;
  }
  return res;
}
